import {useQuery} from '@tanstack/react-query'
import * as blocks from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import type {ReactElement} from 'react'
import {useState} from 'react'
import {useWatch} from 'react-hook-form'

import {useFetchAddressPin} from '../../../api/address'
import {useFetchPickupPoints} from '../../../api/address/services/pickupPointService/pickupPoint'
import {useConfigContext} from '../../../contexts/ConfigContext'
import type {AddressPin, PickupPoint, PickupPoints, PickupTarget} from '../types'
import {type Props as AddressSlotProps, AddressSlot} from './AddressSlot/AddressSlot'
import * as classes from './MapModal.classes'
import {MapModePoints} from './MapModePoints/MapModePoints'
import {MapModeSearch} from './MapModeSearch/MapModeSearch'
import {toBoundsLocation, toCenterLocation} from './utils'

type Props = {
  target: PickupTarget
  pickupPoint?: PickupPoint
  onClose: VoidFunction
  onChange(pickupPoint: PickupPoint): void
}
type MapMode = 'search on map' | 'show points'

export function MapModal(props: Props): ReactElement {
  const {onClose, onChange, pickupPoint, target} = props
  const {location: configLocation} = useConfigContext()
  const coordinates: blocks.LngLat = pickupPoint?.coordinates || configLocation
  const i18n = useI18n()
  const isBusiness: boolean = useWatch({name: 'is_business'})

  const margin = blocks.useMargin()
  const [mode, setMode] = useState<MapMode>('show points')
  const [location, setLocation] = useState<blocks.MapLocationRequest>(toCenterLocation(coordinates, 8))

  const {
    mutate: fetchPickupPoints,
    data: points,
    isPending: isPointsPending,
  } = useFetchPickupPoints({
    onSuccess: pointsToTransit => {
      if (isPickupPointsWithNearest(pointsToTransit)) {
        setLocation(toBoundsLocation(pointsToTransit?.nearest.bounds))
      } else if (isPickupPointsWithBounds(pointsToTransit)) {
        setLocation(toBoundsLocation(pointsToTransit?.bounds))
      }
    },
  })

  const {mutateAsync: fetchAddressPin, data: addressPin, isPending: isAddressPinPending} = useFetchAddressPin()

  useQuery({
    queryKey: ['fetch-points-init'],
    queryFn: () => {
      void fetchAddressPin({coordinates, initial: true}).then(newAddressPin => {
        fetchPickupPoints({addressPin: newAddressPin, isSource: target === 'source', isBusiness})
      })
    },
  })

  const isPending = isPointsPending || isAddressPinPending

  return (
    <blocks.Map
      errorMessage={i18n('map_load_error', 'Не удалось загрузить карту')}
      lang="ru_RU"
      location={location}
      margin={margin}
      zoomStrategy="zoomToCenter"
    >
      {mode === 'show points' && (
        <>
          <Header
            addressPin={addressPin}
            coordinates={coordinates}
            isPending={isPending}
            onAddressPinChange={(addressPinChanged: AddressPin): void => {
              void fetchAddressPin(addressPinChanged).then(newAddressPin => {
                fetchPickupPoints({addressPin: newAddressPin, isSource: target === 'source', isBusiness})
              })
            }}
            onClose={onClose}
            onMapButtonClick={() => {
              addressPin && setLocation(toCenterLocation(addressPin.coordinates, 16))
              setMode('search on map')
            }}
            target={target}
          />
          <blocks.MapGeolocationCustomControl
            classes={classes.geolocationButton}
            initialLocation={coordinates}
            onGeolocatePosition={(locationCoords: blocks.LngLat): void => {
              void fetchAddressPin({coordinates: locationCoords}).then(newAddressPin => {
                fetchPickupPoints({addressPin: newAddressPin, isSource: target === 'source', isBusiness})
              })
            }}
          />
          <MapModePoints
            addressPin={addressPin}
            onLocationChange={setLocation}
            onPickupPointChange={onChange}
            points={points}
            target={target}
          />
        </>
      )}
      {mode === 'search on map' && (
        <MapModeSearch
          addressPin={addressPin}
          onAddressPinChange={(addressPinChanged: AddressPin): void => {
            setMode('show points')
            void fetchAddressPin({coordinates, addressPin: addressPinChanged}).then(newAddressPin => {
              fetchPickupPoints({addressPin: newAddressPin, isSource: target === 'source', isBusiness})
            })
          }}
          onBackButtonClick={() => {
            setMode('show points')
          }}
        />
      )}
    </blocks.Map>
  )
}

type HeaderPros = {
  coordinates: blocks.LngLat
  target: PickupTarget
  isPending: boolean
  addressPin?: AddressPin
  onClose: VoidFunction
  onMapButtonClick: AddressSlotProps['onMapButtonClick']
  onAddressPinChange: AddressSlotProps['onCityChange']
}

function Header(props: HeaderPros): ReactElement {
  const i18n = useI18n()
  const {
    coordinates,
    target,
    addressPin,
    isPending,
    onClose: closeHandler,
    onMapButtonClick: mapButtonClickHandler,
    onAddressPinChange: addressPinChangeHandler,
  } = props
  const isDestination = target === 'destination'
  const ref = blocks.useMarginRef<HTMLDivElement>(el => ({
    top: el.offsetHeight,
  }))

  return (
    <blocks.Paper ref={ref} className={classes.head} flexDirection="column" float="top" pt="xs">
      <blocks.Slot size="l">
        <blocks.Lead onClick={closeHandler}>
          <blocks.ArrowLeftIcon />
        </blocks.Lead>
        <blocks.Text
          // @ts-expect-error Property 'Component' does not exist on type...
          Component={blocks.Typography}
          thickness="medium"
          variant="title4"
        >
          {isDestination
            ? i18n('bdui.pickup_points.pickup-points', 'Пункты выдачи')
            : i18n('bdui.pickup_points.dropoff-points', 'Пункты приёма')}
        </blocks.Text>
      </blocks.Slot>
      <AddressSlot
        addressPin={addressPin}
        coordinates={coordinates}
        isPending={isPending}
        onCityChange={addressPinChangeHandler}
        onMapButtonClick={mapButtonClickHandler}
      />
    </blocks.Paper>
  )
}
function isPickupPointsWithNearest(points: PickupPoints) {
  return 'nearest' in points
}
function isPickupPointsWithBounds(points: PickupPoints) {
  return 'bounds' in points
}
